import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider, MutationCache } from '@tanstack/react-query';
import App from './App';
import './styles/globals.css';
import { toast } from './stores/toast';
import { ApiClientError, registerAuthClearedHandler, registerQuotaExceededHandler } from './lib/api';
import { useAuthStore } from './stores/auth';
import { useQuotaModalStore } from './stores/quotaModal';
import { activeEnvBadge } from './components/EnvironmentBadge';

// 非本番環境と一目で区別できるようタブタイトルに prefix を付与 (staging/demo共通)
if (activeEnvBadge) {
  document.title = `[${activeEnvBadge.shortLabel}] ${document.title}`;
}

// v0.3 (S-6.5): 開発専用の横オーバーフロー検出器。動的importにすることでproduction buildから
// 完全にtree-shakeされる (import.meta.env.DEV が静的にfalseの場合、Viteがこのブロックごと除去する)
if (import.meta.env.DEV) {
  import('./lib/dev-overflow-detector').then(({ initDevOverflowDetector }) => initDevOverflowDetector());
  import('./lib/dev-maxlen').then(({ initDevMaxLenBadge }) => initDevMaxLenBadge());
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      // デフォルトのエラー処理：mutation個別のonErrorが未定義の場合トースト表示
    },
  },
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      // mutation側で onError が明示的に定義されていればスキップ
      if (mutation.options.onError) return;
      const msg = error instanceof ApiClientError
        ? `❌ ${error.message}` + (error.status === 403 ? '（権限がありません）' : '')
        : `❌ 通信エラー：${(error as Error).message}`;
      toast.error(msg);
    },
    onSuccess: (_data, _vars, _ctx, mutation) => {
      // 成功時：mutation個別で onSuccess を定義していない場合のみトースト
      if (mutation.options.onSuccess) return;
      toast.success('✅ 保存しました');
    },
  }),
});

// 401/ログアウト時のクリーンアップ：Zustand ユーザ状態に加えて TanStack Query の全キャッシュも破棄する。
// これが無いと、テナントを跨いでログインし直した際に組織ツリー等の機密データが旧テナントの
// キャッシュ経由で staleTime 分 (最大5分) 表示され得る (S-9 QA レビュー HIGH 指摘対応)
registerAuthClearedHandler(() => {
  useAuthStore.getState().setUser(null);
  queryClient.clear();
});

// v0.3.x (AIトークン利用上限管理): 429 AI_QUOTA_EXCEEDED を検知したら QuotaExceededModal を開く
registerQuotaExceededHandler((info) => {
  useQuotaModalStore.getState().open(info);
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
